import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { authAPI } from "@/lib/api";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/DashboardLayout";
import { resetExpenseRole } from "@/lib/useExpenseRole";
import Dashboard from "@/pages/Dashboard";
import "@/App.css";
import { useResponsiveTables } from "@/lib/useResponsiveTables";

// Lazy load pages for better performance (Dashboard loaded eagerly as entry page)
const Cranes = lazy(() => import("@/pages/Cranes"));
const CraneHistory = lazy(() => import("@/pages/CraneHistory"));
const Operations = lazy(() => import("@/pages/Operations"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const RoomRent = lazy(() => import("@/pages/RoomRent"));
const Revenue = lazy(() => import("@/pages/Revenue"));
const CashFlow = lazy(() => import("@/pages/CashFlow"));
const Payments = lazy(() => import("@/pages/Payments"));
const Loans = lazy(() => import("@/pages/Loans"));
const Maintenance = lazy(() => import("@/pages/Maintenance"));
const Clients = lazy(() => import("@/pages/Clients"));
const Sites = lazy(() => import("@/pages/Sites"));
const SiteSummary = lazy(() => import("@/pages/SiteSummary"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Users = lazy(() => import("@/pages/Users"));
const Employees = lazy(() => import("@/pages/Employees"));
const Settings = lazy(() => import("@/pages/Settings"));
const EmployeeExpenses = lazy(() => import("@/pages/EmployeeExpenses"));
const ExpenseApprovals = lazy(() => import("@/pages/ExpenseApprovals"));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex flex-col items-center justify-center h-64 gap-3">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    <p className="text-sm text-slate-500">Loading...</p>
  </div>
);

function App() {
  useResponsiveTables();
  // Read the stored session synchronously so a hard reload of a deep route is not redirected to /login -> / first
  const [user, setUser] = useState(() => {
    try {
      return localStorage.getItem('token') ? JSON.parse(localStorage.getItem('user') || 'null') : null;
    } catch {
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!user);

  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    authAPI.getMe().then(({ data }) => {
      const merged = { ...JSON.parse(localStorage.getItem('user') || '{}'), ...data };
      localStorage.setItem('user', JSON.stringify(merged));
      setUser(merged);
    }).catch(() => {});
  }, []);

  const handleLogin = (token, userData) => {
    resetExpenseRole();
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    resetExpenseRole();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  const hasAccess = (moduleKey) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!user.allowed_modules) return true;
    return user.allowed_modules.includes(moduleKey);
  };

  const MODULE_HOME = { dashboard: '/', cranes: '/cranes', operations: '/operations', finance: '/expenses', maintenance: '/maintenance', clients: '/clients', sites: '/sites', employees: '/employees', analytics: '/analytics', expense_requests: '/expense-requests' };
  const homePath = user?.role === 'admin' || !user?.allowed_modules ? '/' : (MODULE_HOME[Object.keys(MODULE_HOME).find(k => user.allowed_modules.includes(k))] || '/settings');
  const Guard = ({ moduleKey, children }) => hasAccess(moduleKey) ? children : <Navigate to={homePath === '/' ? '/settings' : homePath} replace />;

  const isEmployeeOnly = user?.allowed_modules?.length === 1 && user?.allowed_modules[0] === 'expense_requests';

  // Employee-only view (mobile-first expense submission)
  if (isAuthenticated && isEmployeeOnly) {
    return (
      <>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="*" element={<EmployeeExpenses user={user} onLogout={handleLogout} />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </>
    );
  }

  return (
    <>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<DashboardLayout user={user} onLogout={handleLogout} />}>
              <Route index element={<Guard moduleKey="dashboard"><Dashboard /></Guard>} />
              <Route path="cranes" element={<Guard moduleKey="cranes"><Cranes /></Guard>} />
              <Route path="cranes/:craneId/history" element={<Guard moduleKey="cranes"><CraneHistory /></Guard>} />
              <Route path="operations" element={<Guard moduleKey="operations"><Operations /></Guard>} />
              <Route path="expenses" element={<Guard moduleKey="finance"><Expenses /></Guard>} />
              <Route path="room-rent" element={<Guard moduleKey="finance"><RoomRent /></Guard>} />
              <Route path="revenue" element={<Guard moduleKey="finance"><Revenue /></Guard>} />
              <Route path="cashflow" element={<Guard moduleKey="finance"><CashFlow /></Guard>} />
              <Route path="payments" element={<Guard moduleKey="finance"><Payments /></Guard>} />
              <Route path="loans" element={<Guard moduleKey="finance"><Loans /></Guard>} />
              <Route path="maintenance" element={<Guard moduleKey="maintenance"><Maintenance /></Guard>} />
              <Route path="clients" element={<Guard moduleKey="clients"><Clients /></Guard>} />
              <Route path="sites" element={<Guard moduleKey="sites"><Sites /></Guard>} />
              <Route path="sites/:siteId" element={<Guard moduleKey="sites"><SiteSummary /></Guard>} />
              <Route path="employees" element={<Guard moduleKey="employees"><Employees /></Guard>} />
              <Route path="analytics" element={<Guard moduleKey="analytics"><Analytics /></Guard>} />
              {user?.role === 'admin' && <Route path="users" element={<Users />} />}
              <Route path="expense-approvals" element={<ExpenseApprovals />} />
              <Route path="expense-requests" element={<Guard moduleKey="expense_requests"><EmployeeExpenses user={user} /></Guard>} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster />
    </>
  );
}

export default App;